/* eslint-disable max-lines-per-function */

import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';

import { Translate } from '@dreryk/react-common';
import {
   IconSize,
   IconWrapper,
   Link,
   RotateRightIcon,
   StrongText,
   StyledFlex,
   Text,
   TextSeparator,
   TextSeparatorType,
} from '@dreryk/react-components';

import {
   OnRefundSuccessCallback,
   RefundAdvancePaymentShowModalEventInterceptor,
} from '~libs.desktop.shared.refund-advance-payment';

import { visitSettlementTranslationKeys } from '../../../translations';
import { AdjustmentFormBean } from '../../form/adjustment/adjustment.types';
import { VisitDetailsFormBean } from '../../form/visitDetails/visitDetails.types';

import { useMedicalServicesPrices } from './MedicalServicesPrices.hooks';
import { StyledPriceText } from './MedicalServicesPrices.styled';
import {
   amountDueSectionViewVisitor,
   isRefundingOnlinePaymentsAvailable,
   medicalServicesPricesVisitor,
} from './MedicalServicesPrices.utils';

const t = visitSettlementTranslationKeys.shared.medicalServices;

type MedicalServicesPricesProps = Readonly<{
   adjustment?: AdjustmentFormBean;
   medicalServicesPrices: number[];
   advancePayments: FinancialSettlementAdvancePayment[];
   visitDetails: VisitDetailsFormBean;
   onRefundSuccess?: OnRefundSuccessCallback;
}>;

// eslint-disable-next-line max-lines-per-function
export const MedicalServicesPrices = (props: MedicalServicesPricesProps): JSX.Element => {
   const { totalPriceAmount, amountDue } = useMedicalServicesPrices({
      advancePayments: props.advancePayments,
      medicalServicesPrices: props.medicalServicesPrices,
   });

   return (
      <StyledFlex direction={'column'} gap={4} alignItems={'flex-end'}>
         {medicalServicesPricesVisitor(props.advancePayments)({
            withAdvancePayment: () => (
               <StyledFlex gap={8}>
                  <StyledFlex direction={'column'} alignItems={'flex-end'} gap={2}>
                     <Text testId={'totalPriceLabel'}>
                        <Translate>{t.totalPrice.label}</Translate>
                        {': '}
                     </Text>

                     {props.advancePayments.map((advancePayment) => (
                        <StyledFlex key={advancePayment.initialTransactionId} alignItems='center' gap={4}>
                           {isRefundingOnlinePaymentsAvailable(
                              advancePayment.transactionExternalId,
                              props.adjustment
                           ) ? (
                              <RefundAdvancePaymentShowModalEventInterceptor
                                 advancePayment={advancePayment}
                                 beginDate={props.visitDetails.beginDate}
                                 endDate={props.visitDetails.endDate}
                                 patientFirstName={props.visitDetails.patient.firstName}
                                 patientLastName={props.visitDetails.patient.lastName}
                                 totalPriceAmount={totalPriceAmount}
                                 onSuccess={props.onRefundSuccess}
                              >
                                 <Link
                                    icon={
                                       <IconWrapper testId={'rotateRightIcon'} size={IconSize.SMALL}>
                                          <RotateRightIcon />
                                       </IconWrapper>
                                    }
                                    testId={'refundAdvancePayment'}
                                    label={t.refundAdvancePayment.label}
                                 />
                              </RefundAdvancePaymentShowModalEventInterceptor>
                           ) : null}

                           <Text testId={'advancePaymentLabel'}>
                              <Translate>{t.advancePayment.label}</Translate>
                              {': '}
                           </Text>
                        </StyledFlex>
                     ))}

                     {amountDueSectionViewVisitor(amountDue)({
                        forPositive: () => (
                           <StrongText testId={'amountDueLabel'}>
                              <Translate>{t.amountDue.label}</Translate>
                              {': '}
                           </StrongText>
                        ),
                        forNegative: () => (
                           <StrongText testId={'overpaymentLabel'}>
                              <Translate>{t.overpaymentAmount.label}</Translate>
                              {': '}
                           </StrongText>
                        ),
                     })}
                  </StyledFlex>
                  <StyledFlex direction={'column'} alignItems={'flex-end'} justifyContent={'space-between'}>
                     <StyledPriceText testId={'totalPrice'} value={totalPriceAmount} />

                     {props.advancePayments.map((advancePayment) => (
                        <StyledPriceText
                           key={advancePayment.initialTransactionId}
                           testId={'advancePayment'}
                           value={advancePayment.remainingAmount.asNumber()}
                        />
                     ))}

                     <strong>
                        <StyledPriceText testId={'amountDue'} value={Math.abs(amountDue)} />
                     </strong>
                  </StyledFlex>
               </StyledFlex>
            ),

            withoutAdvancePayment: () => (
               <TextSeparator separator={TextSeparatorType.COLON_SPACE}>
                  <StrongText testId={'label'}>
                     <Translate>{t.amountDue.label}</Translate>
                  </StrongText>
                  <strong>
                     <StyledPriceText testId={'price'} value={Math.max(0, amountDue)} />
                  </strong>
               </TextSeparator>
            ),
         })}
      </StyledFlex>
   );
};
